// Mirrors the web-api project contracts. The portal consumes these types only —
// the runtime data layer is hand-written RTK Query.

export type ProjectStatus = 'Active' | 'Archived';

export type ProjectDirectoryRow = {
  projectId: string;
  name: string;
  status: ProjectStatus;
  displayColour: string;
  defaultBillable: boolean;
  taskCount: number;
  activeTaskCount: number;
  tagCount: number;
};

// Query parameters for GET /clients/{clientId}/projects (UC-42 §6.a).
// `tenantId` is never sent — the server resolves it from the subdomain (NFR-038).
// Omitted optional fields apply no filter per §5.2.a.
export type ListProjectsByClientParams = {
  clientId: string;
  nameContains?: string;
  status?: ProjectStatus;
  defaultBillable?: boolean;
};

// UC-43 — Create Project under Client (POST /clients/{clientId}/projects).
// `clientId` is a path parameter; `tenantId` is resolved server-side (NFR-038).
export type CreateProjectRequest = {
  clientId: string;
  name: string;
  displayColour: string;
  defaultBillable: boolean;
  idempotencyKey?: string;
};

export type CreateProjectResponse = {
  projectId: string;
  tenantId: string;
  clientId: string;
  name: string;
  displayColour: string;
  defaultBillable: boolean;
  status: 'Active';
  createdAt: string;
  createdBy: string;
};

// Phase-4 stack decision: 18-colour platform palette (ProjectColourPalette.cs).
// Values must match the server-side KnownValues set exactly (case-insensitive on the
// server, but we always send lower-case hex which the server stores as-is).
export const PROJECT_COLOUR_PALETTE = [
  '#E03131', '#C2255C', '#9C36B5', '#6741D9', '#3B5BDB', '#1971C2',
  '#0C8599', '#2F9E44', '#66A80F', '#F08C00', '#E8590C', '#2E2E2E',
  '#868E96', '#1098AD', '#37B24D', '#F59F00', '#D9480F', '#5C940D',
] as const;

export type ProjectColour = (typeof PROJECT_COLOUR_PALETTE)[number];

// Full Project detail row returned by GET /projects/{projectId} (UC-44 supporting
// read — catalog gap OQ-SS-09). Carries the page-computed TeamLead-assignment flag
// so the page can gate the Metadata section without an extra query.
// `hasTimeEntries` is the server-side VAL-032 gate flag: true when ≥1 Time entries
// of any status reference this Project (UC-46 §5.web-portal.7 narrowing #1).
export type ProjectDetail = {
  projectId: string;
  tenantId: string;
  clientId: string;
  name: string;
  displayColour: string;
  defaultBillable: boolean;
  status: ProjectStatus;
  hasTimeEntries: boolean;
  archivedAt: string | null;
  archivedBy: string | null;
  createdAt: string;
  createdBy: string;
  lastModifiedAt: string | null;
  lastModifiedBy: string | null;
  isActingPrincipalAssignedAsTeamLead: boolean;
};

// UC-46 — DELETE /projects/{projectId}.
export type DeleteProjectRequest = {
  projectId: string;
  clientId: string; // captured client-side for post-success navigation and tag invalidation
  tenantId: string; // captured client-side for tag invalidation
};

// UC-44 — PUT /projects/{projectId}.
// Only changed fields need to be supplied; omitting a field leaves it unchanged.
export type EditProjectRequest = {
  projectId: string;
  name?: string;
  displayColour?: string;
  defaultBillable?: boolean;
  idempotencyKey?: string;
};

// Success body for PUT /projects/{projectId} → 200 OK (UC-44 §6.a).
export type EditProjectResponse = {
  projectId: string;
  tenantId: string;
  clientId: string;
  name: string;
  displayColour: string;
  defaultBillable: boolean;
  status: ProjectStatus;
  lastModifiedAt: string | null;
  lastModifiedBy: string | null;
};

// UC-45 — POST /projects/:projectId/archive (§6.web-api)
export type ArchiveProjectRequest = {
  projectId: string;
  idempotencyKey?: string;
};

// The archive response is the full updated ProjectDetail (same shape as GET).
export type ArchiveProjectResponse = ProjectDetail;
